import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import api from "../../services/api";
import { ENDPOINTS } from "../../services/endpoints";
import { IMAGES } from "../../constants/image";
import loginConfig from "../../config/forms/login.json";
import { validateValue } from "../../utils/validation";
import { useAuth } from "../../context/AuthContext";
import usePopup from "../../hooks/usePopup";
import { extractMessageResponse } from "../../utils/messageResponse";
interface FieldValidation {
  label?: string;
  required?: boolean;
  pattern?: string;
  minLength?: number;
  maxLength?: number;
}
interface FormField {
  name: string;
  type: string;
  label?: string;
  placeholder?: string;
  validation?: FieldValidation;
}
const Login: React.FC = () => {
  const [formData, setFormData] = useState<any>({});
  const [errors, setErrors] = useState<any>({});
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const navigate = useNavigate();
  const { login } = useAuth();
  const showPopup = usePopup();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/data-visualization";
  const fields = loginConfig.fields as FormField[];
  const ICON_MAP: Record<string, string> = {
    email: IMAGES.ICONS.EMAIL,
    password: IMAGES.ICONS.PASSWORD,
  };
  const toggleIconMap: Record<string, string> = {
    password: IMAGES.CHANGEPASSWORD.EYE_ICON,
  };

  useEffect(() => {
    const sessionExpiredMessage = sessionStorage.getItem(
      "sessionExpiredMessage",
    );

    if (sessionExpiredMessage) {
      sessionStorage.removeItem("sessionExpiredMessage");
      showPopup({ status: 403, message: sessionExpiredMessage });
    }
  }, [showPopup]);

  const getApiMessage = (apiData: any) => {
    const { message } = extractMessageResponse(apiData, "", 500);
    return message;
  };

  const handleChange = (e: any) => {
    const { name, value } = e.target;

    //update form data
    setFormData((prev: any) => ({
      ...prev,
      [name]: value,
    }));

    //validate only this field
    const errorMsg = validateField(name, value);

    setErrors((prev: any) => ({
      ...prev,
      [name]: errorMsg,
    }));
  };
  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    fields.forEach((field) => {
      const value = formData[field.name];

      const error = validateValue(value, {
        label: field.label,
        ...(field.validation || {}),
      });

      if (error) newErrors[field.name] = error;
    });

    return newErrors;
  };

  const validateField = (name: string, value: string) => {
    const field = fields.find((f) => f.name === name);
    if (!field) return "";

    const error = validateValue(value, {
      label: field.label,
      ...(field.validation || {}),
    });

    return error || "";
  };
  const handleCancel = () => {
    setFormData({});
    setErrors({});
  };
  const handleSubmit = async (e: any) => {
    e.preventDefault();

    const validationErrors = validateForm();

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setErrors({});
    setIsSubmitting(true);

    try {
      const data = {
        email: formData.email,
        password: formData.password,
      };

      const response = await api.post(ENDPOINTS.AUTH.LOGIN, data);
      const apiData = response?.data;
      const messageResponse = extractMessageResponse(apiData, "Login failed", 500);
      const responseCode = messageResponse.status;
      const token = apiData?.token?.remember_token;
      const userDetails = apiData?.userDetails;
      const sessionConfig = apiData?.sessionConfig;
      if (token) {
        login(token, userDetails || null);
      }
      // Store session config for InactivityHandler
      if (sessionConfig) {
        sessionStorage.setItem("sessionConfig", JSON.stringify(sessionConfig));
      }
      showPopup({
        status: responseCode,
        message: messageResponse.message,
        messageType: messageResponse.messageType,
        messageCode: messageResponse.messageCode,
        onOk:
          responseCode === 200
            ? () => navigate(from, { replace: true })
            : undefined,
      });
    } catch (error: any) {
      const apiData = error?.response?.data;
      const messageResponse = extractMessageResponse(apiData, "Login failed", 500);
      const responseCode = messageResponse.status;
      const message = messageResponse.message;
      setErrors((prev: any) => ({
        ...prev,
        apiError: message,
      }));
      showPopup({
        status: responseCode,
        message,
        messageType: messageResponse.messageType,
        messageCode: messageResponse.messageCode,
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  return (
    <div
      className="login-wrapper"
      // style={{ backgroundImage: `url(${ICON_MAP["login_bg"]})` }}
    >
      <div className="login-background">
        <div className="login-card-container">
          <div className="card login-card shadow">
            {/* Title */}
            <div className="card-body p-3">
              <h3 className="text-center fw-bold text-white mb-2">
                Welcome Back
              </h3>
              <p className="text-center text-white mb-3">
                Please enter your details to access your account.
              </p>

              <form onSubmit={handleSubmit}>
                {fields.map((field) => {
                  const value = formData[field.name] || "";
                  const error = errors[field.name];

                  return (
                    <div className="mb-3 position-relative" key={field.name}>
                      {/* Label */}
                      <label className="form-label">{field.label}</label>

                      <div className="login-input-group">
                        {/* Input */}
                        <input
                          type={
                            field.name === "password"
                              ? showPassword
                                ? "text"
                                : "password"
                              : field.type
                          }
                          name={field.name}
                          className={`form-control custom-input ${error ? "is-invalid" : ""}`}
                          placeholder={field.placeholder}
                          value={value}
                          onChange={handleChange}
                        />

                        {/* Icon */}
                        {ICON_MAP[field.name] && (
                          <img
                            src={ICON_MAP[field.name]}
                            alt={field.name}
                            className="input-icon"
                          />
                        )}

                        {field.name === "password" && (
                          <img
                            src={
                              showPassword
                                ? toggleIconMap[field.name]
                                : IMAGES.CHANGEPASSWORD.EYE_HIDE_ICON
                            }
                            alt={showPassword ? "Show password" : "Hide password"}
                            className="toggle-icon"
                            onClick={() => setShowPassword(!showPassword)}
                          />
                        )}
                      </div>

                      {/* Error */}
                      {error && (
                        <div className="invalid-feedback d-block">{error}</div>
                      )}
                    </div>
                  );
                })}

                {/* Forgot Password */}
                <div className="text-end mb-3">
                  <Link to="/forgot-password" className="forgot-link">
                    Forgot password?
                  </Link>
                </div>

                {/* Buttons */}
                <div className="d-flex gap-2 login-action-row">
                  <button
                    type="submit"
                    className="btn btn-primary w-100 login-action-btn"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Processing..." : "Sign In"}
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-secondary w-100 login-action-btn login-cancel-btn"
                    onClick={handleCancel}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </button>
                </div>
              </form>

              {/* Footer */}
              <div className="text-center mt-4">
                <span className="text-muted">Don't have an account? </span>
                <Link to="/signup" className="signup-link">
                  Sign up for free
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
